import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useApiClient } from '@/utils/api/ApiClient'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { emptyPageData, PageData } from 'api/Pagination/Types'
import { RawTransactionDto } from 'api/Transaction/Types/RawTransactionDto'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getTransactionOverviewForWalletOrClientFields } from 'components/Filter/getTransactionOverviewForWalletOrClientFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { CustomColumnMenu } from 'routes/Records/Clients/CustomColumnMenu'
import { WalletNavigation, WalletTab } from 'routes/Records/Wallets/Navigation/WalletNavigation'
import { normalizeTransactionsSortQuery } from 'routes/Transactions/normalizer'
import { useAppDispatch, useAppSelector } from 'store'
import { GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchTransactionStatusAction,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { PageQuery } from 'utils/api'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import transactionOverviewWalletOrClientColumn, {
  GridColumn,
} from './transactionOverviewWalletOrClientColumn'

interface Props {
  walletId?: string
  clientId: string
  wallet?: WalletDto
  fetchTransactions: (query?: PageQuery) => Promise<PageData<RawTransactionDto[]>>
}

const TransactionOverviewForWalletOrClientTable = ({
  clientId,
  walletId,
  wallet,
  fetchTransactions,
}: Props) => {
  const [auth] = useContext(AuthSessionContext)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const permissions = auth?.permissions || []

  const { gridState, gridActions } = useGridState()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const [transactions, setTransactions] = useState<RawTransactionDto[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const [columns, setColumns] = useState<GridColumn[]>(transactionOverviewWalletOrClientColumn)

  const states = useAppSelector((state) => state.transactions.status)
  const dispatch = useAppDispatch()

  useEffect(() => {
    ;(async () => {
      if (isEmpty(states)) {
        await dispatch(fetchTransactionStatusAction(walletsApiClient))
      }
    })()
  }, [dispatch, states]) // eslint-disable-line react-hooks/exhaustive-deps

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => buildQuery(filter ?? gridState.filter) ?? null,
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTransactionsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<RawTransactionDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await fetchTransactions({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.take,
            skipCount: tableData?.skipCount !== false,
          })

          if (response?.items) {
            setTransactions(response.items.map((x) => Object.assign({ selected: false }, x)))
          }

          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<RawTransactionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const handleColumnsSubmit = (columnsState: GridColumn[]) => {
    setColumns(columnsState)
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await fetchTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await fetchTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTransactions(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await fetchTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response?.totalResults ?? 0

    gridActions.setGridFilters({
      total,
    })

    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await fetchTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount,
    })

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response?.totalResults ?? 0,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleFilterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await fetchTransactions({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response?.totalResults ?? 0

      if (response?.items) {
        setTransactions(response.items.map((x) => Object.assign({ selected: false }, x)))
      }

      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <Fragment>
      {walletId && wallet && (
        <WalletNavigation tab={WalletTab.TRANSACTIONS} isEdit={false} data={wallet} />
      )}
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={handleFilterFetch}
        filterType={FilterType.TransactionOverviewWalletOrClient}
        gridState={gridState}
        filterFields={getTransactionOverviewForWalletOrClientFields(clientId, walletId)}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          data={transactions}
          columns={columns}
          hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportTransactionsModule)}
        >
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            style={{ zIndex: 0 }}
            sortable={{
              allowUnsort: true,
              mode: 'multiple',
            }}
            pageable={pageableSettings}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  columnMenu={(props) => (
                    <CustomColumnMenu
                      {...props}
                      columns={columns}
                      onColumnsSubmit={handleColumnsSubmit}
                    />
                  )}
                  {...column}
                />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </Fragment>
  )
}

interface InnerProps {
  clientId?: string
  wallet?: WalletDto
}

const TransactionOverviewForWalletOrClient: React.FC<InnerProps> = (props) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const params = useParams<{ id: string }>()
  const location = useLocation()
  const walletId = params.id
  const isWalletPath = location.pathname.includes('wallets')

  const transactionTypes = useAppSelector((state) => state.transactions.types)
  const dispatch = useAppDispatch()

  const fetchData = useCallback(async () => {
    if (isWalletPath && !props?.wallet && walletId) {
      return await walletsApiClient.getWalletById(walletId)
    }
  }, [isWalletPath, props, walletId]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data, isLoading } = useFetchOne(fetchData)

  const clientId = props?.clientId || data?.client.id || props.wallet?.client.id
  const wallet = data || props.wallet

  const fetchTransactionsFactory = useCallback(
    async (query?: PageQuery) => {
      if (isEmpty(transactionTypes)) {
        dispatch(fetchTransactionTypeAction(walletsApiClient))
      }
      if (!isWalletPath && clientId) {
        return await walletsApiClient.getWalletTransactionsByClientId(clientId, query)
      }
      if (wallet && walletId) {
        return await walletsApiClient.getWalletTransactions(walletId, query)
      }

      return emptyPageData
    },
    [isWalletPath, clientId, wallet, walletId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  if (isLoading || !clientId) {
    return <Spinner />
  }

  return (
    <TransactionOverviewForWalletOrClientTable
      wallet={wallet}
      walletId={isWalletPath ? walletId : undefined}
      clientId={clientId}
      fetchTransactions={fetchTransactionsFactory}
    />
  )
}

export default TransactionOverviewForWalletOrClient
